import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import useCountUp from '../hooks/useCountUp';
import { useTheme } from '../contexts/ThemeContext';
import { 
  ChefHat, 
  UserCheck, 
  Bike, 
  Car, 
  Zap, 
  ShieldCheck, 
  Smartphone, 
  BadgeDollarSign, 
  Headphones, 
  Lock,
  Star,
  ArrowRight,
  CheckCircle2,
  Menu,
  X,
  Apple,
  Download,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Sparkles,
  Home as HomeIcon,
  Droplets,
  Package,
  Shirt as ShirtIcon,
  CookingPot,
  Sun,
  Moon,
  Calendar,
  Clock,
  Repeat,
  Building2,
  TrendingUp,
  Handshake
} from 'lucide-react';
import { serviceCategories, testimonials, faqs, features, howItWorks } from '../mock';

const iconMap = {
  ChefHat: ChefHat,
  UserCheck: UserCheck,
  Bike: Bike,
  Car: Car,
  Zap: Zap,
  ShieldCheck: ShieldCheck,
  Smartphone: Smartphone,
  BadgeDollarSign: BadgeDollarSign,
  Headphones: Headphones,
  Lock: Lock,
  Sparkles: Sparkles,
  Home: HomeIcon,
  Droplets: Droplets,
  Package: Package,
  ShirtIcon: ShirtIcon,
  CookingPot: CookingPot
};

// Counter component with animation
const CounterStat = ({ end, label, suffix = '', duration = 2500 }) => {
  const { count, countRef } = useCountUp(end, duration);
  
  return (
    <div ref={countRef}>
      <div className="text-3xl font-bold text-gray-900 dark:text-white">
        {count.toLocaleString()}{suffix}
      </div>
      <div className="text-sm text-gray-600 dark:text-gray-300">{label}</div>
    </div>
  );
};

// Animated stat card for stats section
const AnimatedStatCard = ({ end, label, suffix = '' }) => {
  const { count, countRef } = useCountUp(end, 2500);
  
  return (
    <div ref={countRef} className="text-center transform hover:scale-110 transition-transform duration-300">
      <div className="text-5xl md:text-6xl font-bold mb-2">
        {count === 10 ? count : count.toLocaleString()}{suffix}
      </div>
      <div className="text-lg opacity-90">{label}</div>
    </div>
  );
};

const HeroStats = () => {
  return (
    <div className="flex items-center gap-8 pt-4 flex-wrap">
      <CounterStat end={10000} label="App Downloads" suffix="+" />
      <div className="h-12 w-px bg-gray-300"></div>
      <CounterStat end={10} label="Average Response (min)" />
      <div className="h-12 w-px bg-gray-300"></div>
      <CounterStat end={10000} label="Happy Customers" suffix="+" />
    </div>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  
  // Refs for scroll animations
  const observerRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Scroll animation observer (Apple/Snabbit style)
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all sections
    const sections = document.querySelectorAll('.fade-in-section');
    sections.forEach((section) => {
      observerRef.current.observe(section);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0a] transition-colors duration-300">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white/95 dark:bg-[#0a0a0a]/95 backdrop-blur-md shadow-md dark:shadow-gray-900' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png" 
                alt="doorush logo" 
                className="h-10 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-4">
              <button onClick={() => scrollToSection('services')} className="text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium">
                Services
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium">
                Reviews
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium">
                FAQ
              </button>
              <button onClick={() => navigate('/franchise')} className="text-gray-700 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium">
                Franchise
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
                aria-label="Toggle theme"
              >
                {isDark ? <Sun className="text-gray-300" size={20} /> : <Moon className="text-gray-700 dark:text-gray-300" size={20} />}
              </button>
              <a 
                href="https://apps.apple.com/in/app/doorush-services-at-home/id6639613304"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-300 hover:scale-105"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/s5xirbk8_1.png"
                  alt="Download on App Store"
                  className="h-10"
                />
              </a>
              <a 
                href="https://play.google.com/store/apps/details?id=com.doorush.user&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                className="transition-all duration-300 hover:scale-105"
              >
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/4cj06y2v_2.png"
                  alt="Get it on Google Play"
                  className="h-10"
                />
              </a>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden text-gray-700 dark:text-gray-300 hover:text-[#007e7a] transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white dark:bg-[#1a1a1a] border-t border-gray-100 dark:border-gray-700 shadow-lg">
            <div className="px-4 py-6 space-y-4">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium py-2">
                Services
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium py-2">
                Reviews
              </button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium py-2">
                FAQ
              </button>
              <button onClick={() => { navigate('/franchise'); setMobileMenuOpen(false); }} className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium py-2">
                Franchise
              </button>
              <button
                onClick={toggleTheme}
                className="flex items-center gap-2 w-full text-left text-gray-700 dark:text-gray-300 dark:text-gray-300 hover:text-[#007e7a] dark:hover:text-[#00a89d] transition-colors font-medium py-2"
              >
                {isDark ? <Sun size={20} /> : <Moon size={20} />}
                {isDark ? 'Light Mode' : 'Dark Mode'}
              </button>
              <div className="flex flex-col gap-3 pt-4">
                <a 
                  href="https://apps.apple.com/in/app/doorush-services-at-home/id6639613304"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/s5xirbk8_1.png"
                    alt="Download on App Store"
                    className="h-12 w-auto"
                  />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.doorush.user&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/4cj06y2v_2.png"
                    alt="Get it on Google Play"
                    className="h-12 w-auto"
                  />
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#007e7a]/5 via-white to-[#007e7a]/5"></div>
        {/* Animated background shapes */}
        <div className="absolute top-20 right-10 w-64 h-64 bg-[#007e7a]/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#007e7a]/5 rounded-full blur-3xl animate-float" style={{animationDelay: '1s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-[#007e7a]/5 rounded-full blur-2xl animate-float" style={{animationDelay: '2s'}}></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fadeInUp">
              <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 backdrop-blur-sm">
                <Zap className="w-3 h-3 mr-1" /> Instant Help, Anytime
              </Badge>
              
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold text-gray-900 dark:text-white leading-[1.05] tracking-tighter">
                Help at Your
                <span className="block bg-gradient-to-r from-[#007e7a] to-[#00a89d] bg-clip-text text-transparent mt-3">Doorstep</span>
              </h1>
              
              <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 leading-relaxed font-normal max-w-2xl">
                Professional maids, home cooks, and premium cleaning services in minutes. 
                Your trusted partner for all home service needs.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a 
                  href="https://apps.apple.com/in/app/doorush-services-at-home/id6639613304"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 hover:scale-105"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/s5xirbk8_1.png"
                    alt="Download on App Store"
                    className="h-14"
                  />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.doorush.user&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-all duration-300 hover:scale-105"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/4cj06y2v_2.png"
                    alt="Get it on Google Play"
                    className="h-14"
                  />
                </a>
              </div>
              
              <HeroStats />
            </div>
            
            <div className="relative animate-fadeInRight">
              <div className="relative z-10">
                {/* App mockup showcase */}
                <div className="relative flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/u50g9oyr_1.png" 
                    alt="doorush app mockup" 
                    className="w-[450px] drop-shadow-2xl animate-float rounded-3xl"
                  />
                  {/* Floating badges */}
                  <div className="absolute -top-8 -right-8 bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl p-4 animate-float border border-gray-100 dark:border-gray-700" style={{animationDelay: '0.5s'}}>
                    <div className="text-3xl font-bold text-[#007e7a] dark:text-[#00a89d]">10min</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Fast Response</div>
                  </div>
                  <div className="absolute -bottom-4 -left-8 bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-2xl p-4 animate-float border border-gray-100 dark:border-gray-700" style={{animationDelay: '1s'}}>
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">10K+</div>
                    <div className="text-xs text-gray-600 dark:text-gray-300">Downloads</div>
                  </div>
                </div>
              </div>
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-[#007e7a]/20 to-transparent rounded-full blur-3xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-[#1a1a1a] relative overflow-hidden fade-in-section">
        {/* Background decoration */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#007e7a]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#007e7a]/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-20">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 backdrop-blur-sm mb-6">
              Our Services
            </Badge>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight leading-none">
              Everything You Need
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              From daily chores to special needs, we've got you covered with professional services
            </p>
          </div>
          
          {/* Service Categories */}
          <div className="space-y-16">
            {serviceCategories.map((category, catIndex) => (
              <div key={category.id} className="space-y-8">
                {/* Category Header */}
                <div className="text-center mb-4">
                  <h3 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">
                    {category.category}
                  </h3>
                  <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">{category.description}</p>
                </div>
                
                {/* Service Cards */}
                <div className={`grid gap-8 ${
                  category.services.length === 1 
                    ? 'md:grid-cols-1 max-w-lg mx-auto' 
                    : category.services.length <= 3 
                    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                    : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                }`}>
                  {category.services.map((service, index) => {
                    const IconComponent = iconMap[service.icon];
                    return (
                      <Card 
                        key={service.id} 
                        className="group cursor-pointer transition-all duration-700 ease-out hover:scale-[1.02] bg-white dark:bg-[#1a1a1a] overflow-hidden rounded-3xl border border-gray-100 dark:border-gray-800 hover:shadow-2xl hover:border-[#007e7a]/20 dark:hover:border-[#00a89d]/30"
                        style={{ animationDelay: `${(catIndex * 100) + (index * 50)}ms` }}
                        onClick={() => scrollToSection('download')}
                      >
                        <div className="relative h-72 overflow-hidden">
                          <img 
                            src={service.image} 
                            alt={service.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity duration-500"></div>
                          <div className="absolute inset-0 flex flex-col justify-end p-8">
                            <div className="transform translate-y-0 group-hover:-translate-y-2 transition-transform duration-500">
                              <div className="flex items-center gap-3 mb-3">
                                <div className="w-14 h-14 bg-white/10 backdrop-blur-xl rounded-2xl flex items-center justify-center flex-shrink-0 border border-white/20 group-hover:bg-white/20 transition-all duration-500">
                                  <IconComponent className="text-white" size={28} />
                                </div>
                              </div>
                              <h4 className="text-white font-bold text-2xl mb-2 tracking-tight">{service.title}</h4>
                              <p className="text-white/90 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500 delay-100">
                                {service.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Showcase Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20">
                <Smartphone className="mr-1" size={14} />
                Smart Technology
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white">
                Everything in One App
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                Seamlessly book services, track arrivals, make payments, and rate your experience - all from your smartphone.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 dark:bg-[#1a1a1a] transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Instant Booking</h3>
                    <p className="text-gray-600 dark:text-gray-300">Book any service in seconds with our intuitive interface</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 dark:bg-[#1a1a1a] transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Real-time Tracking</h3>
                    <p className="text-gray-600 dark:text-gray-300">Track your service provider's arrival in real-time</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 dark:bg-[#1a1a1a] transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Lock className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">Secure Payments</h3>
                    <p className="text-gray-600 dark:text-gray-300">Multiple payment options with end-to-end encryption</p>
                  </div>
                </div>
              </div>
              <div className="pt-4">
                <Button 
                  size="lg"
                  className="bg-[#007e7a] hover:bg-[#006666] text-white"
                  onClick={() => scrollToSection('download')}
                >
                  <Download className="mr-2" size={20} />
                  Download App Now
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="relative z-10 grid grid-cols-2 gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/5bd37mur_2.png"
                  alt="App mockup 1"
                  className="rounded-3xl shadow-2xl animate-float w-full object-cover"
                />
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/gqo9hykc_3.png"
                  alt="App mockup 2"
                  className="rounded-3xl shadow-2xl animate-float mt-12 w-full object-cover"
                  style={{animationDelay: '0.5s'}}
                />
              </div>
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#007e7a]/10 rounded-full blur-3xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Recurring Booking Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-[#1a1a1a] relative overflow-hidden fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 backdrop-blur-sm">
                <Repeat className="mr-1" size={14} />
                Recurring Bookings
              </Badge>
              <h2 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white dark:text-white tracking-tight leading-none">
                Plan Your Bookings With Ultimate Customization
              </h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 dark:text-gray-300 leading-relaxed">
                Schedule services from days to weeks or months. Set it once, and we'll handle the rest with flexible recurring options tailored to your needs.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6 pt-4">
                <div className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-[#1a1a1a] dark:bg-[#1a1a1a] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white dark:text-white mb-1">Daily Bookings</h3>
                    <p className="text-gray-600 dark:text-gray-300 dark:text-gray-400 text-sm">Schedule services every day</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-[#1a1a1a] dark:bg-[#1a1a1a] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white dark:text-white mb-1">Weekly Plans</h3>
                    <p className="text-gray-600 dark:text-gray-300 dark:text-gray-400 text-sm">Book for specific days of the week</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-[#1a1a1a] dark:bg-[#1a1a1a] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Repeat className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white dark:text-white mb-1">Monthly Services</h3>
                    <p className="text-gray-600 dark:text-gray-300 dark:text-gray-400 text-sm">Long-term recurring bookings</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 p-4 rounded-2xl bg-gray-50 dark:bg-[#1a1a1a] dark:bg-[#1a1a1a] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#007e7a]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="text-[#007e7a]" size={24} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white dark:text-white mb-1">Custom Schedule</h3>
                    <p className="text-gray-600 dark:text-gray-300 dark:text-gray-400 text-sm">Create your own flexible plan</p>
                  </div>
                </div>
              </div>
              
              <div className="pt-4">
                <Button 
                  size="lg"
                  className="bg-[#007e7a] hover:bg-[#006666] text-white"
                  onClick={() => scrollToSection('download')}
                >
                  <Download className="mr-2" size={20} />
                  Start Recurring Bookings
                </Button>
              </div>
            </div>
            
            <div className="relative">
              <div className="relative z-10">
                <img 
                  src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/rkxqejv7_4.png"
                  alt="Recurring bookings"
                  className="rounded-3xl shadow-2xl animate-float w-full"
                />
              </div>
              <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#007e7a]/10 dark:bg-[#00a89d]/10 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-[#007e7a]/5 dark:bg-[#00a89d]/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 dark:from-gray-800 to-white dark:to-gray-900 relative overflow-hidden fade-in-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 backdrop-blur-sm mb-6">
              Simple Process
            </Badge>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight leading-none">
              How It Works
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Get help in three simple steps. It's that easy!
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 md:gap-12 relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-12 left-1/4 right-1/4 h-1 bg-gradient-to-r from-[#007e7a]/20 via-[#007e7a]/50 to-[#007e7a]/20"></div>
            
            {howItWorks.map((step, index) => (
              <div 
                key={step.id} 
                className="relative text-center group"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="relative inline-block mb-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-[#007e7a] to-[#005555] rounded-3xl flex items-center justify-center text-white text-3xl font-bold shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 relative z-10">
                    {step.step}
                  </div>
                  <div className="absolute inset-0 bg-[#007e7a]/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-300"></div>
                  {index < howItWorks.length - 1 && (
                    <ArrowRight className="hidden md:block absolute top-1/2 -right-20 text-[#007e7a]/40 group-hover:text-[#007e7a] group-hover:translate-x-2 transition-all duration-300" size={40} />
                  )}
                </div>
                <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl p-8 shadow-sm group-hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-800">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-lg leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#007e7a] to-[#005555] text-white relative overflow-hidden fade-in-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            <AnimatedStatCard end={10000} label="App Downloads" suffix="+" />
            <AnimatedStatCard end={10000} label="Happy Customers" suffix="+" />
            <AnimatedStatCard end={10} label="Avg Response Time" suffix=" min" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-[#1a1a1a] relative overflow-hidden fade-in-section">
        {/* Background decoration */}
        <div className="absolute top-20 left-0 w-96 h-96 bg-gradient-to-br from-[#007e7a]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-gradient-to-br from-[#007e7a]/10 to-transparent rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-20">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 backdrop-blur-sm mb-6">
              Why Choose Us
            </Badge>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight leading-none">
              Built For Your Convenience
            </h2>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              We combine technology with reliability to deliver exceptional service
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div 
                  key={feature.id} 
                  className="group relative bg-white dark:bg-[#1a1a1a] rounded-3xl p-8 hover:shadow-xl transition-all duration-700 ease-out hover:scale-[1.02] border border-gray-100 dark:border-gray-800 hover:border-[#007e7a]/20 dark:hover:border-[#00a89d]/30 cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="relative">
                    <div className="w-16 h-16 bg-[#007e7a]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#007e7a] transition-all duration-500">
                      <IconComponent className="text-[#007e7a] group-hover:text-white transition-colors duration-500" size={28} />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3 tracking-tight">{feature.title}</h3>
                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden fade-in-section">
        {/* Background elements */}
        <div className="absolute top-0 left-0 w-full h-full">
          <img 
            src="https://images.unsplash.com/photo-1522441815192-d9f04eb0615c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxncmFkaWVudCUyMGJhY2tncm91bmR8ZW58MHx8fHRlYWx8MTc2NTgwNDM4NHww&ixlib=rb-4.1.0&q=85"
            alt="Background"
            className="w-full h-full object-cover opacity-5"
          />
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 mb-4">
              Testimonials
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Join thousands of satisfied customers who trust doorush
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <Card className="p-8 md:p-12 border-2 border-[#007e7a]/10 dark:border-[#00a89d]/20 shadow-2xl bg-white/95 dark:bg-[#1a1a1a]/95 backdrop-blur-sm relative overflow-hidden">
              {/* Decorative corner */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#007e7a]/10 to-transparent rounded-bl-full"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-[#007e7a]/10 to-transparent rounded-tr-full"></div>
              
              <div className="flex flex-col md:flex-row items-center gap-8 relative">
                <div className="relative">
                  <img 
                    src={testimonials[activeTestimonial].image} 
                    alt={testimonials[activeTestimonial].name}
                    className="w-32 h-32 rounded-full border-4 border-[#007e7a]/20 shadow-xl"
                  />
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-[#007e7a] rounded-full flex items-center justify-center shadow-lg">
                    <Star className="text-white fill-white" size={20} />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex justify-center md:justify-start gap-1 mb-4">
                    {[...Array(testimonials[activeTestimonial].rating)].map((_, i) => (
                      <Star key={i} className="text-yellow-500 fill-yellow-500" size={24} />
                    ))}
                  </div>
                  <p className="text-2xl text-gray-700 dark:text-gray-300 mb-6 italic leading-relaxed">
                    "{testimonials[activeTestimonial].text}"
                  </p>
                  <div>
                    <div className="font-bold text-xl text-gray-900 dark:text-white">
                      {testimonials[activeTestimonial].name}
                    </div>
                    <div className="text-[#007e7a] font-medium">
                      {testimonials[activeTestimonial].role}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
            
            <div className="flex justify-center gap-3 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial 
                      ? 'bg-[#007e7a] w-12 shadow-lg' 
                      : 'bg-gray-300 hover:bg-gray-400 w-3'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 dark:bg-[#1a1a1a] fade-in-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-[#007e7a]/10 text-[#007e7a] hover:bg-[#007e7a]/20 border-[#007e7a]/20 mb-4">
              FAQ
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Everything you need to know about doorush
            </p>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem 
                key={faq.id} 
                value={`item-${faq.id}`} 
                className="bg-white dark:bg-[#1a1a1a] border-2 border-gray-100 dark:border-gray-800 rounded-xl px-6 hover:border-[#007e7a]/20 dark:hover:border-[#00a89d]/30 transition-colors"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 dark:text-white hover:text-[#007e7a] hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 dark:text-gray-300 text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Download CTA Section */}
      <section id="download" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007e7a] to-[#005555] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <Badge className="bg-white/20 text-white mb-4 backdrop-blur-sm">
                <Download className="mr-1" size={14} />
                Download Now
              </Badge>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                Get doorush App Today
              </h2>
              <p className="text-xl mb-8 opacity-90 leading-relaxed">
                Join 10,000+ users who trust doorush for instant home services. Download now and get Exclusive Offers on your first booking!
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start mb-6">
                <a 
                  href="https://apps.apple.com/in/app/doorush-services-at-home/id6639613304" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="transition-all duration-300 hover:scale-105"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/s5xirbk8_1.png"
                    alt="Download on App Store"
                    className="h-16"
                  />
                </a>
                <a 
                  href="https://play.google.com/store/apps/details?id=com.doorush.user&pcampaignid=web_share" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="transition-all duration-300 hover:scale-105"
                >
                  <img 
                    src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/4cj06y2v_2.png"
                    alt="Get it on Google Play"
                    className="h-16"
                  />
                </a>
              </div>
              
              <div className="flex items-center gap-4 justify-center lg:justify-start text-sm opacity-75">
                <div>10K+ Downloads</div>
                <div>•</div>
                {/* <div>10K+ Happy Customers</div> */}
                <div>•</div>
                <div>Free to Download</div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_instant-chef-2/artifacts/ccsowwmw_1.png"
                alt="doorush app"
                className="w-full max-w-md mx-auto drop-shadow-2xl animate-float rounded-3xl"
              />
              <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/20 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/20 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Franchise Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007e7a]/5 to-white dark:from-[#007e7a]/10 dark:to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#007e7a]/10 text-[#007e7a] dark:bg-[#007e7a]/20 dark:text-[#00a89d] mb-4">
                Franchise Opportunity
              </Badge>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                Own a doorush <span className="text-[#007e7a] dark:text-[#00a89d]">Franchise</span>
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                Partner with India's fastest-growing on-demand home services platform. Low investment, high returns, and full operational support from doorush.
              </p>
              <div className="grid grid-cols-3 gap-6 mb-8">
                <div>
                  <div className="text-2xl font-bold text-[#007e7a] dark:text-[#00a89d]">6L - 50L</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Investment based on location</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#007e7a] dark:text-[#00a89d]">10-24</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Months Payback</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-[#007e7a] dark:text-[#00a89d]">3L - 30L</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">Annual Returns</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => navigate('/franchise')}
                  size="lg"
                  className="bg-[#007e7a] hover:bg-[#006666] text-white text-lg px-8 py-6"
                >
                  Explore Franchise <ArrowRight className="ml-2" size={18} />
                </Button>
                <a
                  href="https://wa.me/919445875911?text=Hi%20doorush%2C%20I%27m%20interested%20in%20your%20franchise%20opportunity.%20Please%20share%20more%20details."
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-[#007e7a] text-[#007e7a] hover:bg-[#007e7a] hover:text-white text-lg px-8 py-6"
                  >
                    Become a Partner
                  </Button>
                </a>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="relative">
                <div className="w-72 h-72 bg-gradient-to-br from-[#007e7a] to-[#005555] rounded-3xl flex items-center justify-center shadow-2xl">
                  <div className="text-center text-white p-8">
                    <Building2 size={56} className="mx-auto mb-4 opacity-90" />
                    <p className="text-xl font-bold mb-1">City Franchise</p>
                    <p className="text-sm opacity-80">Two flexible models</p>
                    <p className="text-sm opacity-80">Passive & Hybrid</p>
                  </div>
                </div>
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-orange-400/90 rounded-2xl flex items-center justify-center shadow-lg">
                  <TrendingUp size={28} className="text-white" />
                </div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-[#007e7a]/80 rounded-2xl flex items-center justify-center shadow-lg">
                  <Handshake size={28} className="text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 dark:bg-[#0a0a0a] text-gray-400 dark:text-gray-500 py-12 px-4 sm:px-6 lg:px-8 border-t border-gray-800 dark:border-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_ec527428-3aec-42a4-a1f7-d2e66c0be0df/artifacts/hktez0bk_logo_transs.png" 
                alt="doorush" 
                className="h-10 mb-4 brightness-200 cursor-pointer"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              />
              <p className="text-sm mb-4">
                Your trusted partner for instant home services. Quality help at your doorstep.
              </p>
              <div className="flex gap-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#007e7a] transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#007e7a] transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#007e7a] transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="hover:text-[#007e7a] transition-colors">
                  <Linkedin size={20} />
                </a>
              </div>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => scrollToSection('services')} className="hover:text-[#007e7a] transition-colors text-left">House Help</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-[#007e7a] transition-colors text-left">Instant Cook</button></li>
                <li><button onClick={() => scrollToSection('services')} className="hover:text-[#007e7a] transition-colors text-left">Bike & Car Care</button></li>
                <li><button onClick={() => scrollToSection('download')} className="hover:text-[#007e7a] transition-colors text-left">Download App</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-[#007e7a] transition-colors text-left">About Us</button></li>
                <li><button onClick={() => navigate('/teams')} className="hover:text-[#007e7a] transition-colors text-left">Our Team</button></li>
                <li><button onClick={() => navigate('/investors')} className="hover:text-[#007e7a] transition-colors text-left">Investors</button></li>
                <li><button onClick={() => navigate('/franchise')} className="hover:text-[#007e7a] transition-colors text-left">Franchise</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-[#007e7a] transition-colors text-left">Contact Us</button></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-white font-semibold mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy-policy')} className="hover:text-[#007e7a] transition-colors text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms-conditions')} className="hover:text-[#007e7a] transition-colors text-left">Terms & Conditions</button></li>
                <li><button onClick={() => navigate('/refund-policy')} className="hover:text-[#007e7a] transition-colors text-left">Refund Policy</button></li>
                <li><button onClick={() => navigate('/contact')} className="hover:text-[#007e7a] transition-colors text-left">Support</button></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 text-center text-sm">
            <p>© 2025 doorush. All rights reserved. Made with ❤️ in Chennai</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
